/* eslint-disable complexity */
import React, {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styles';
import { Loader } from '@unmind/design-system-components-web';
import useInfiniteScroll, { Direction } from 'hooks/useInfiniteScroll';
import useFeatureFlag from 'flags/useFeatureFlag';
import { DropdownMenu, MenuItem } from 'Navigation/AccountMenu/DropdownMenu';
import { MenuItemConfig } from 'Navigation/AccountMenu/AccountMenu';
import { Trash, Message, NovaInfo } from 'icons';
import { useUserFeedbackQuery } from 'Shared/FeedbackBanner/useUserFeedbackQuery';
import { getUserLocale } from 'i18n/getUserLocale';
import { FEEDBACK_LOCALES } from 'i18n/feedbackLocales';
import { isMSTeams } from 'utils/MSTeams/MSTeamsHelpers';
import { ConversationUpdateType, ScrollLocation, UserMessage } from '../types';
import {
  useConversationHistory,
  useSubmitInitialMessage,
  useIsScrollAtBottom,
} from '../hooks';
import { messagesToConversationSessions } from '../utils';
import { useAuth0Token } from '../hooks/useAuth0Token';
import { ChatbotInput } from './ChatbotInput';
import { ChatbotSession } from './ChatbotSession';
import {
  ChatbotFooter,
  ChatBotFooterText,
  ChatBotInputContainer,
  ChatbotMessageWrapper,
  ChatbotWrapper,
  LoadingMoreErrorWrapper,
  LoadingMoreWrapper,
  FeatureUnavailableErrorWrapper,
  ScrollIconContainer,
  ScrollIcon,
  SettingsMenuWrapper,
  SettingsIcon,
  SettingsMenuItem,
  ClearChatLabel,
  FeedbackLink,
} from './styles';
import { Error } from './Error';
import { ClearChatModal } from './ClearChatModal';
import { getFeedbackFormLink } from './ChatbotFeedback/utils';
import { AboutNovaModal } from './AboutNovaModal';
import { TransparencyMessage } from './TransparencyMessage';

const FEEDBACK_SURVEY_LINK = 'https://unmind.typeform.com/to/ISVJNakZ';

const CONVERSATION_UPDATE_SCROLL_MAPPING: Record<
  ConversationUpdateType,
  ScrollLocation
> = {
  NEW_MESSAGE: 'BOTTOM',
  LOAD_MORE: 'CURRENT',
};

export interface ChatbotProps {
  /**
   * An optional initial message from landing page redirect
   */
  initialMessage?: UserMessage;
}

const redirectToSignIn = () => {
  const SIGNIN_PATH = '/?redirect=';
  const signinUrl = `${window.location.origin}${SIGNIN_PATH}${window.location.pathname}`;
  window.location.assign(signinUrl);
};

export const Chatbot = ({ initialMessage }: ChatbotProps) => {
  const theme = useTheme();

  const { t: translate } = useTranslation('chatbot');
  const [shouldSubmitInitialMessage, setSubmitInitialMessage] =
    useSubmitInitialMessage();
  const { isScrollAtBottom, resetScrollAtBottom } = useIsScrollAtBottom(500);
  const novaInitiatesConversationFollowUp = useFeatureFlag(
    'nova-initiates-conversation-follow-up',
  );
  const [hasSentFollowUp, setHasSentFollowUp] = useState(false);
  const [clearChatModalOpen, setClearChatModalOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [aboutNovaModalOpen, setAboutNovaModalOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isMultilineInput, setIsMultilineInput] = useState(false);

  const userLocale = getUserLocale();
  const isInMSTeams = isMSTeams();

  const toggleSettingsMenu = () => {
    setSettingsMenuOpen(!settingsMenuOpen);
  };

  const closeSettingsMenu = () => {
    setSettingsMenuOpen(false);
  };

  const { data } = useUserFeedbackQuery();
  const feedbackFormLink = getFeedbackFormLink(FEEDBACK_SURVEY_LINK, data);

  const [currentHeight, setCurrentHeight] = useState(
    document.body.scrollHeight,
  );
  const [scrollAction, setScrollAction] = useState<ScrollLocation>('BOTTOM');

  // Reference Objects
  const pageRef: MutableRefObject<null> = useRef(null);

  function scrollToEnd() {
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight || window.innerHeight,
      });
    }, 10);
  }

  const handleConversationUpdated: Parameters<
    typeof useConversationHistory
  >[0]['onConversationUpdated'] = updateType => {
    setScrollAction(CONVERSATION_UPDATE_SCROLL_MAPPING[updateType]);
  };

  const {
    messages,
    conversationId,
    isSubmittingMessage,
    isStreamingMessage,
    isLoadingHistory,
    hadErrorSubmittingMessage,
    hadErrorLoadingHistory,
    transparencyCheckRequired,
    submitMessage,
    resubmitMessages,
    loadMoreMessages,
    retryLoadingHistory,
    submitFollowUpMessage,
    handleAcknowledgeTransparencyCheck,
    clearConversationHistory,
  } = useConversationHistory({
    onConversationUpdated: handleConversationUpdated,
  });

  // Update the useEffect that handles initial loading to set isInitialLoad to false
  useEffect(() => {
    if (!isLoadingHistory && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoadingHistory, isInitialLoad]);

  const settingsMenuItemsConfig: MenuItemConfig[] = [
    {
      key: 'about-nova',
      label: (
        <SettingsMenuItem>
          <NovaInfo />
          {translate('modals.about_nova.title')}
        </SettingsMenuItem>
      ),
      onSelect: () => setAboutNovaModalOpen(true),
    },
    {
      key: 'share-feedback',
      label: (
        <SettingsMenuItem>
          <Message />
          <FeedbackLink href={feedbackFormLink} target="_blank">
            {translate('modals.settings.feedback_button.label')}
          </FeedbackLink>
        </SettingsMenuItem>
      ),
      isEnabled: FEEDBACK_LOCALES.includes(userLocale),
    },
    {
      key: 'clear-chat',
      label: (
        <SettingsMenuItem>
          <Trash />
          <ClearChatLabel>
            {translate('modals.settings.clear_chat_button.label')}
          </ClearChatLabel>
        </SettingsMenuItem>
      ),
      onSelect: () => setClearChatModalOpen(true),
      isEnabled: messages.length !== 0,
    },
  ];

  const onOptionSelected = (menuItem: MenuItemConfig) => () => {
    const { onSelect } = menuItem;

    if (onSelect) {
      onSelect();
    }
  };

  const menuItems = settingsMenuItemsConfig
    .filter(({ isEnabled }) => isEnabled !== false)
    .map(menuItem => (
      <MenuItem
        data-testid={`menu-item-${menuItem.key}`}
        aria-label={menuItem.label}
        key={menuItem.key}
        label={menuItem.label}
        onClick={onOptionSelected(menuItem)}
        isInNovaLUI={true}
        {...menuItem.overrides}
      />
    ));

  /**
   * Only recalculate sessions if messages change not for any other re render behavior
   */
  const sessions = useMemo(
    () => messagesToConversationSessions(messages),
    [messages],
  );

  /**
   * This effect handles a initial message from the home page redirect
   * It will submit the message if the history is loaded and the initial message
   * is set and if the shouldSubmitInitialMessage is true, the latter of the conditionals
   * prevents it from resubmitting the initial message on page reload.
   */
  useEffect(() => {
    if (
      initialMessage &&
      shouldSubmitInitialMessage &&
      !isLoadingHistory &&
      !transparencyCheckRequired
    ) {
      void setSubmitInitialMessage(false);
      void submitMessage(initialMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingHistory,
    initialMessage,
    shouldSubmitInitialMessage,
    transparencyCheckRequired,
  ]);

  const isLatestSectionEmpty =
    sessions[sessions.length - 1].messages.length === 0;
  /**
   * This effect handles the novaInitiatesConversationFollowUp feature flag
   */
  useEffect(() => {
    if (
      novaInitiatesConversationFollowUp &&
      messages.length !== 0 &&
      isLatestSectionEmpty &&
      !isStreamingMessage &&
      !isSubmittingMessage &&
      !isLoadingHistory &&
      !hasSentFollowUp &&
      !initialMessage
    ) {
      setHasSentFollowUp(true);
      void submitFollowUpMessage();
    }
  }, [
    hasSentFollowUp,
    isLatestSectionEmpty,
    isLoadingHistory,
    isStreamingMessage,
    isSubmittingMessage,
    messages.length,
    novaInitiatesConversationFollowUp,
    submitFollowUpMessage,
    initialMessage,
  ]);
  /**
   * This useEffect hook handles the scroll position when new messages are added to the chat
   * and historical messages are loaded via infinite scroll.
   */
  useEffect(() => {
    if (sessions && sessions.length > 0) {
      if (scrollAction === 'BOTTOM') {
        scrollToEnd();
        setCurrentHeight(document.body.scrollHeight);
      } else if (scrollAction === 'CURRENT') {
        setTimeout(() => {
          // Calculate new scroll position
          const newHeight = document.body.scrollHeight;

          const newScrollPosition = newHeight - currentHeight;

          // Restore the scroll position
          window.scrollTo(0, newScrollPosition);
          setCurrentHeight(newHeight);
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  useInfiniteScroll(
    () => {
      setTimeout(() => {
        void loadMoreMessages();
      }, 500);
    },
    pageRef,
    Direction.UP,
  );

  /**
   * If the user's session has expired or Chat API returns 401, redirect to sign in
   */
  const auth0Token = useAuth0Token();
  const unmindAuthToken = localStorage.getItem('token');
  if (auth0Token !== undefined) {
    if (
      !(auth0Token || unmindAuthToken) ||
      hadErrorLoadingHistory === 'USER_SESSION_EXPIRED'
    ) {
      redirectToSignIn();
    }
  }

  /**
   * If the history is unavailable, show the feature unavailable screen
   */
  if (hadErrorLoadingHistory === 'FEATURE_UNAVAILABLE') {
    return (
      <ChatbotWrapper>
        <FeatureUnavailableErrorWrapper>
          <Error
            title={translate('conversation.something_went_wrong.title')}
            body={translate('conversation.something_went_wrong.body')}
            orientation="vertical"
          />
        </FeatureUnavailableErrorWrapper>
      </ChatbotWrapper>
    );
  }

  if (
    transparencyCheckRequired &&
    !isLoadingHistory &&
    !hadErrorLoadingHistory
  ) {
    return (
      <ChatbotWrapper>
        <TransparencyMessage
          handleAcknowledgeButtonPress={async () =>
            handleAcknowledgeTransparencyCheck(Boolean(initialMessage))
          }
        />
        <SettingsMenuWrapper>
          <DropdownMenu
            aria-label={translate('conversation.open_settings_button.label')}
            a11y-role="button"
            onClick={toggleSettingsMenu}
            open={settingsMenuOpen}
            onRequestClose={closeSettingsMenu}
            icon={<SettingsIcon />}
            isInNovaLUI={true}
            isInVerticalMenu={false}
          >
            {menuItems}
          </DropdownMenu>
        </SettingsMenuWrapper>
        {aboutNovaModalOpen && (
          <AboutNovaModal onClose={() => setAboutNovaModalOpen(false)} />
        )}
        {clearChatModalOpen && (
          <ClearChatModal
            conversationId={conversationId}
            onClose={() => {
              setClearChatModalOpen(false);
            }}
            onClearChat={async () => {
              resetScrollAtBottom();
              setClearChatModalOpen(false);
              void clearConversationHistory();
            }}
            redirectToSignIn={redirectToSignIn}
          />
        )}
      </ChatbotWrapper>
    );
  }

  return (
    <ChatbotWrapper>
      {isLoadingHistory && isInitialLoad ? (
        <LoadingMoreWrapper
          aria-label={translate('conversation.loading.a11y_label')}
        >
          <Loader
            color={theme.colors.chatbot.messages.loading.bubbles}
            size={4}
          />
        </LoadingMoreWrapper>
      ) : hadErrorLoadingHistory ? (
        <LoadingMoreErrorWrapper>
          <Error
            title={translate(
              'conversation.conversation_errors.fetch_conversation_history_error.title',
            )}
            onTryAgainPress={retryLoadingHistory}
          />
        </LoadingMoreErrorWrapper>
      ) : (
        <>
          <ChatbotMessageWrapper>
            {isLoadingHistory && !isInitialLoad && (
              <LoadingMoreWrapper
                aria-label={translate('conversation.loading.a11y_label')}
              >
                <Loader
                  color={theme.colors.chatbot.messages.loading.bubbles}
                  size={4}
                />
              </LoadingMoreWrapper>
            )}
            {sessions &&
              sessions.map(({ date, messages: sessionMessages }, index) => (
                <ChatbotSession
                  key={date.toISOString().split('T')[0]}
                  isCurrentOldestLoadedSession={
                    index === 0 && sessions.length >= 1
                  }
                  novaInitiatesConversationFollowUp={
                    novaInitiatesConversationFollowUp
                  }
                  date={date}
                  messages={sessionMessages}
                  loading={index === sessions.length - 1 && isSubmittingMessage}
                  error={
                    index === sessions.length - 1 && hadErrorSubmittingMessage
                  }
                  onSubmit={submitMessage}
                  onReSubmit={resubmitMessages}
                  hasInitialMessage={Boolean(initialMessage)}
                />
              ))}
          </ChatbotMessageWrapper>

          {!isScrollAtBottom && messages.length > 1 ? (
            <ScrollIconContainer
              a11y-role="button"
              aria-label={translate('conversation.scroll_to_bottom.a11y_label')}
              onClick={() => {
                setScrollAction('BOTTOM');
                scrollToEnd();
              }}
              $isMultilineInput={isMultilineInput}
              $isMSTeams={isInMSTeams}
            >
              <ScrollIcon />
            </ScrollIconContainer>
          ) : null}

          <ChatbotFooter $isMSTeams={isInMSTeams}>
            <ChatBotInputContainer>
              <ChatbotInput
                canSubmit={
                  !isSubmittingMessage &&
                  !isLoadingHistory &&
                  !isStreamingMessage
                }
                onSubmit={submitMessage}
                setMultilineInputState={setIsMultilineInput}
              />
            </ChatBotInputContainer>
            <ChatBotFooterText>
              {translate('conversation.conversation_footer_text')}
            </ChatBotFooterText>
          </ChatbotFooter>
        </>
      )}

      <SettingsMenuWrapper>
        <DropdownMenu
          aria-label={translate('conversation.open_settings_button.label')}
          a11y-role="button"
          onClick={toggleSettingsMenu}
          open={settingsMenuOpen}
          onRequestClose={closeSettingsMenu}
          icon={<SettingsIcon />}
          isInNovaLUI={true}
          isInVerticalMenu={false}
        >
          {menuItems}
        </DropdownMenu>
      </SettingsMenuWrapper>
      {aboutNovaModalOpen && (
        <AboutNovaModal onClose={() => setAboutNovaModalOpen(false)} />
      )}
      {clearChatModalOpen && (
        <ClearChatModal
          conversationId={conversationId}
          onClose={() => {
            setClearChatModalOpen(false);
          }}
          onClearChat={async () => {
            resetScrollAtBottom();
            setClearChatModalOpen(false);
            void clearConversationHistory();
          }}
          redirectToSignIn={redirectToSignIn}
        />
      )}
    </ChatbotWrapper>
  );
};
